/**
 * CASTLE ATLAS — Sales & Negotiation Landing Page
 * ═══════════════════════════════════════════════════════
 * VISUAL REDESIGN — "Negotiation Agreement" antique-document theme.
 * Replaces the previous blue "Agreement of Skill" paper theme with a more
 * detailed antique-document version of the same idea: aged parchment cards
 * (paper-texture.jpg), two torn-edge paper-sheet images as the hero backdrop
 * (hero-paper-left.png behind the text column, hero-contract-panel.png a
 * photographic desk scene beside it — round 15; before that, a single
 * full-bleed pen-and-ink engraving, manifesto-background.png, round 11-14;
 * before THAT, a dark photo, desk-background.jpg, through round 10 — both
 * fully retired now, no references left anywhere in this file), pen-and-ink
 * engraved scenario icons,
 * a hand-drawn progress ring standing in for a literal wax seal in most
 * spots (WobblyProgressRing — round 12 has it in StatsColumn), the real
 * Castle Atlas seal (castle-atlas-seal.png, /brand/ — round 19; wax-
 * seal.png held this spot from round 13 through round 18, retired and
 * deleted this round, no references left anywhere in this file) as
 * NegotiationAgreement's header badge (round 12 placed a seal in the
 * signature row; round 13 moved it up into the header as the card's big
 * focal element, swapping with the status StampBadge — see that
 * component), and an accent color local to this page only (round 2 made
 * it red; round 6 reverted it to blue, matching the app-wide color
 * everywhere else — see ACCENT below).
 *
 * Round 13 — real alpha on every icon PNG. wax-seal.png and all 6
 * pen-and-ink icons (4 scenario icons, the Deal Intelligence rook, the
 * quill) shipped as opaque RGB with no alpha channel at all (PNG color
 * type 2 — confirmed by reading each file's own header, not assumed from
 * how they rendered). That's invisible in light mode, where the baked-in
 * white canvas multiplies away against a light card, but breaks in dark
 * mode two different ways depending on which blend mode a given icon
 * happened to use: 'screen' reveals the white canvas as a solid box
 * (mode-independent — screen(white, anything) = white); plain 'multiply'
 * compounds with ParchmentCard's own dark-mode overlay and reads as
 * "too dark to make out" instead. Every icon file got chroma-keyed to
 * real alpha (white canvas -> transparent, verified via each output
 * file's own pixel data, not just visually) so mixBlendMode: isDark ?
 * 'screen' : 'multiply' — now applied consistently everywhere an icon on
 * this page is used — behaves correctly in both themes with no artifact
 * either way.
 *
 * Structural change from the previous version: the old ColdHero/WarmHero
 * split (two mutually-exclusive full hero designs swapped on `hasStarted`)
 * is now two ALWAYS-VISIBLE stacked sections — Hero (pitch) always on top,
 * Negotiation Agreement (terms + signature/progress) always below it. Only
 * the CONTENT inside the Agreement card still varies by state (awaiting
 * signature vs. real progress) — the section structure itself is now fixed.
 *
 * Everything data/routing-related is untouched: useProgress(), the
 * skillExercises/overallPct/avgScore/getScenarioDone/radarData computations,
 * getRandomCharacter() + enterVoiceRoom() navigation, and all 5 named CTAs
 * (hero button, 4 scenario cards, final CTA button) still fire the exact
 * same handlers to the exact same destinations as before.
 *
 * The 4th scenario's DISPLAY title changed from "Enterprise Closing" to
 * "Price & Closing" to match the generated icon (icon-price-closing.png)
 * and the reference spec's naming — its `id` ('enterprise-closing') is
 * unchanged, since that id is the routing key into getRandomCharacter()'s
 * scenario buckets.
 *
 * Typography: kept Barlow Condensed (--font-skill-sales) for headlines/
 * brand identity — that's the same face Dashboard/Skills/Sidebar already
 * use for this skill's identity elsewhere in the app, and changing it here
 * would make this page inconsistent with those surfaces. Leaned harder into
 * the Georgia serif stack (already used in this file for ContractField
 * values) for contract-document body text — terms, dates, the live-demo
 * quote — to push the antique-document feel without introducing a new font
 * load or colliding with another skill's dedicated face (DM Serif Display
 * is Copywriting's identity, Lora is Storytelling's).
 *
 * Drop path: frontend/src/features/sales-negotiation/pages/SalesNegotiationLanding.jsx
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@hooks/usePageTitle';
import { useProgress } from '@hooks/useProgress';
import { useDarkMode } from '@hooks/useDarkMode';
import { useAuth } from '@contexts/AuthContext';
import StampBadge from '@components/StampBadge';
import { getRandomCharacter, CHARACTER_COUNTS, SCENARIOS as CHARACTER_POOLS, isScenarioLive } from '@features/sales-negotiation/data';
import {
  TrendingUp, Brain, Phone, Scale, Mic,
} from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar,
  ResponsiveContainer, Tooltip,
} from 'recharts';

/* ─── TOKENS ─── */
// Round 6: reverted back to the app-wide blue (#0369a1 — same value as
// color_primary in the skills table / Sidebar ROUTE_TINTS /
// SKILL_CARD_REGISTRY). Round 2 had changed this to a page-local red
// (#9a2020); round 5 reverted every OTHER surface (skill card, sidebar,
// dashboard) back to blue but deliberately left this page's own ACCENT
// red pending confirmation. Now confirmed — single token, still easy to
// change, just back to matching everywhere else.
const ACCENT = '#0369a1';

const ASSET = '/sales-negotiation';

/* ─── THEMED ICON ART ──────────────────────────────────────────
   Registry of every icon that can have a dedicated dark-mode variant,
   keyed by scenario id (the 4 scenario cards) plus one extra key for the
   Deal Intelligence rook, which isn't a scenario but follows the exact
   same light/dark rule through the same helper.

   icon-cold-call.png and icon-objection-defense.png were found reverted
   to their real engraved originals this round too — both had been
   overwritten in place with the same neutral-grey pencil art that hit
   icon-deal-intelligence.png (confirmed via git history: both shrank from
   multi-MB engravings to ~350-800KB pencil sketches). Restored from git
   history the same way, with the pencil versions kept as their -dark
   variants rather than discarded, since that art already exists and are
   the intended dark-mode source.

   negotiation-tactics and enterprise-closing have no dark variant yet —
   both are being replaced later, so dark:null on purpose; they keep
   their original light art + mixBlendMode in both themes until then. */
const ICON_ART = {
  'cold-call-mastery':   { light: 'icon-cold-call.png',          dark: 'icon-cold-call-dark.png' },
  'objection-defense':   { light: 'icon-objection-defense.png',  dark: 'icon-objection-defense-dark.png' },
  'negotiation-tactics': { light: 'icon-negotiation-tactics.png', dark: null },
  'enterprise-closing':  { light: 'icon-price-closing.png',       dark: null },
  'deal-intelligence':   { light: 'icon-deal-intelligence.png',  dark: 'icon-deal-intelligence-dark.png' },
};

/* Tunable in isolation from the helper below: sepia/saturate/hue-rotate
   screenshotted at 0.35, 0.5 and 0.7 for a side-by-side pick before
   settling at 0.5. Round 20 added brightness(0.82): the tinted dark
   variants (phone, shield, rook) were rendering noticeably brighter than
   the untinted engravings still used for negotiation-tactics/price-
   closing (dark:null), so the scenario row read as two loud icons next
   to two dim ones — sepia/saturate both push luminance up on top of
   whatever the source art already had, with nothing pulling it back
   down. Screenshotted at 0.75/0.82/0.90 against the two untinted cards
   in the same row before settling here. */
const DARK_ICON_TINT = 'sepia(0.5) saturate(1.3) hue-rotate(-6deg) brightness(0.82) contrast(0.92)';
/* Interim only — the dark variants are tonal renderings sitting on top of
   the card rather than ink in it, unlike the light set's flat line
   engraving. New art is being generated to fix that properly; until then,
   this softens the sticker effect so the card texture shows through
   rather than trying to disguise it further. No drop-shadow: a shadow
   would reinforce "floating above the surface," the exact problem.
   Screenshotted at 0.88 and 0.78 (see the round's own report) before
   settling here. */
const DARK_ICON_OPACITY = 0.88;

/* One shared rule set for every icon in ICON_ART, so the scenario cards
   and the Deal Intelligence rook can't drift into different logic for the
   same decision. A dark variant, when one exists and the page is in dark
   mode, needs NO mixBlendMode — it's real alpha art drawn for a dark
   ground already, not a light engraving being blended onto one — plus the
   warm tint so its neutral pencil grey doesn't read cold against the
   page's warm parchment, and reduced opacity so it sits into the card
   rather than sitting on it. Anything else (no dark variant, or light
   mode) uses the light file with its existing mixBlendMode behaviour,
   exactly as before this registry existed. */
const getIconArt = (key, isDark) => {
  const entry = ICON_ART[key];
  if (isDark && entry.dark) {
    return { src: `${ASSET}/${entry.dark}`, style: { filter: DARK_ICON_TINT, opacity: DARK_ICON_OPACITY } };
  }
  return { src: `${ASSET}/${entry.light}`, style: { mixBlendMode: isDark ? 'screen' : 'multiply' } };
};

// Shared container math so the hero's content and every section below it
// line up on the exact same left/right edges at every viewport width —
// not just coincidentally matching padding values.
// Round 8 cut this to 8px/10px chasing a "flush to the sidebar" look, which
// turned out to be the wrong lever — the actual reason the hero read as
// inset was a broken full-bleed calc() fighting the sidebar layout (fixed
// in Hero's own comment), not this padding being too big. At 8px that
// value left no real safety margin against normal glyph overhang on a
// large bold uppercase headline, and combined with the calc() bug it
// clipped the leading letter of "CLOSE" and "NEGOTIATE" outright — real
// content loss, not just tight spacing. Back to a real margin. Hero and
// every section below it still share this exact constant, so their left
// edges line up by construction — not by matching a measured px value.
const pagePad = (isMobile) => (isMobile ? '0 20px' : '0 24px');

const T = {
  radius: { sm: 6, md: 10, lg: 14, xl: 18 },
  trans: 'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
  font: {
    sans: 'var(--font-body)',
    cond: 'var(--font-skill-sales)', // Barlow Condensed — kept for brand-identity continuity with Dashboard/Skills/Sidebar
    mono: 'var(--font-mono)',
    script: 'var(--font-script, "Caveat", cursive)', // handwritten — matches the dashboard/skills contract card CTA
    doc: 'Georgia, "Times New Roman", serif', // antique-document body text — already used in this file pre-redesign
  },
};

/* ─── SCENARIO DATA ───
   iconSrc dropped — ScenarioCard now resolves its icon (and whether it
   gets a dark variant) through getIconArt(id, isDark), keyed off `id`. */
/* `reps` is the size of that scenario's character pool, read from the data
   (CHARACTER_COUNTS), never typed in: 45 + 40 + 40 + 35 = 160. These were
   hardcoded 20/25/25/25, which is where the page's "95" came from. */
const SCENARIOS = [
  {
    id: 'cold-call-mastery', num: '01', title: 'Cold Call Mastery',
    desc: 'Break through gatekeepers and hook decision-makers in the first 15 seconds.',
    reps: CHARACTER_COUNTS['cold-call-mastery'],
  },
  {
    id: 'objection-defense', num: '02', title: 'Objection Defense',
    desc: 'Turn "too expensive" and "we already have a vendor" into open doors.',
    reps: CHARACTER_COUNTS['objection-defense'],
  },
  {
    id: 'negotiation-tactics', num: '03', title: 'Negotiation Tactics',
    desc: 'Anchor high, trade concessions, and hold your margin under pressure.',
    reps: CHARACTER_COUNTS['negotiation-tactics'],
  },
  {
    // id stays 'enterprise-closing' — this is the routing key into
    // getRandomCharacter()'s scenario buckets, unchanged. Only the display
    // title/tag/copy changed to match icon-price-closing.png's naming.
    id: 'enterprise-closing', num: '04', title: 'Price & Closing',
    desc: 'Defend your price, navigate procurement, and close six-figure deals.',
    reps: CHARACTER_COUNTS['enterprise-closing'],
  },
];

/* Scenarios people can play today. The others are shown as "coming soon" and are left out of every total below
   (the hero's rep count, progress, resume target, the radar): 45 + 40 = 85 while two scenarios are not open. */
const LIVE_SCENARIOS = SCENARIOS.filter((sc) => isScenarioLive(sc.id));

const SCENARIO_SKILL_MAP = {
  'cold-call-mastery': 'Opening',
  'objection-defense': 'Objections',
  'negotiation-tactics': 'Closing',
  'enterprise-closing': 'Strategy',
};

/* Per-scenario CTA verb — replaces generic "Start"/"Continue" on cards.
   Cheap, real, no fake data: just more specific action copy per scenario. */
const CTA_VERBS = {
  'cold-call-mastery': 'Take the Call',
  'objection-defense': 'Handle the Objection',
  'negotiation-tactics': 'Anchor the Deal',
  'enterprise-closing': 'Close the Deal',
};

/* Primary CTA copy (hero button + final CTA button) — 3 stages. Pre-start
   copy matches this skill's dashboard/skills card button verbatim
   (config.sales-negotiation.jsx's ContractCard: 'Sign & Begin', see that
   file's CTA row) so the two surfaces read as the same offer before the
   learner has started. 100%-complete stage stays inside the page's
   contract/negotiation framing rather than a generic "Done!". */
const getPrimaryCtaCopy = (hasStarted, overallPct) => {
  if (overallPct >= 100) return 'Deal Closed';
  if (hasStarted) return 'Continue Training';
  return 'Sign & Begin';
};

const RADAR_DATA = [
  { skill: 'Opening', full: 100 },
  { skill: 'Objections', full: 100 },
  { skill: 'Closing', full: 100 },
  { skill: 'Strategy', full: 100 },
  { skill: 'Rapport', full: 100 },
  { skill: 'Value', full: 100 },
];

const CLAUSES = [
  'Read intent behind objections before responding',
  'Anchor high, concede slowly, close with confidence',
  'Turn price resistance into value conversations',
  'Control the frame in multi-stakeholder deals',
];

const DEAL_INTELLIGENCE = [
  'Every rep is scored across 20 negotiation dimensions — tone, pacing, concession timing, and more.',
  'Objection patterns are tracked across sessions, so you see what’s actually holding you back.',
  'AI feedback benchmarks your responses against high-performing negotiation frameworks.',
  'Your skill radar updates in real time as new data comes in from every scenario.',
];

// Derives initials for the signature monogram — same learnerName source as
// the footer's Learner stat, just reduced to 1-2 letters.
const getInitials = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'CA';
  return parts.slice(0, 2).map(p => p[0].toUpperCase()).join('');
};

/* ─── SKELETON ─── */
const SalesSkeleton = ({ isDark }) => {
  const paper = isDark ? '#1f1911' : '#efe6d0';
  return (
    <div style={{ minHeight: '100vh', backgroundColor: paper }}>
      <style>{`
        @keyframes stampIn{0%{opacity:0;transform:scale(1.4) rotate(-12deg)}60%{opacity:1;transform:scale(0.95) rotate(-8deg)}100%{opacity:1;transform:scale(1) rotate(-6deg)}}
        @keyframes fadeSlide{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes shimmerDoc{0%{background-position:200% 0}100%{background-position:-200% 0}}
      `}</style>
      <div className="rail-clearance" style={{
        minHeight: '100vh',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        fontFamily: T.font.cond,
      }}>
        <div style={{ animation: 'fadeSlide 0.5s ease-out both', textAlign: 'center', position: 'relative' }}>
          <div style={{
            width: 76, height: 76, borderRadius: 14, margin: '0 auto 16px',
            border: `2.5px solid ${ACCENT}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'stampIn 0.6s cubic-bezier(0.22,1,0.36,1) both',
            background: `${ACCENT}0d`,
          }}>
            <TrendingUp size={34} color={ACCENT} strokeWidth={2.5} />
          </div>
          <div style={{
            fontSize: 11, color: isDark ? 'rgba(232,220,192,0.5)' : 'rgba(46,36,22,0.5)',
            fontFamily: T.font.mono, marginTop: 4,
          }}>
            loading sales-negotiation<span style={{ animation: 'shimmerDoc 1s infinite' }}>...</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── PARCHMENT CARD — paper-texture.jpg card, replaces the old flat
   gradient ContractShell. isDark darkens the texture with a color-multiply
   overlay rather than swapping images, so both themes still read as "aged
   paper," just lighter or darker paper. ─── */
const ParchmentCard = ({ children, isDark, style = {}, hovered = false, onClick }) => (
  <div onClick={onClick} style={{
    backgroundImage: `linear-gradient(${isDark ? 'rgba(20,15,8,0.82)' : 'rgba(255,252,240,0.14)'}, ${isDark ? 'rgba(20,15,8,0.82)' : 'rgba(255,252,240,0.14)'}), url(${ASSET}/paper-texture.jpg)`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    borderRadius: T.radius.lg,
    border: `1px solid ${hovered ? `${ACCENT}66` : (isDark ? '#4a3f28' : '#c9b78f')}`,
    borderLeft: `3px solid ${hovered ? ACCENT : `${ACCENT}55`}`,
    overflow: 'hidden', cursor: onClick ? 'pointer' : 'default',
    position: 'relative', transition: T.trans,
    boxShadow: hovered
      ? `0 16px 40px ${ACCENT}2a`
      : (isDark ? '0 2px 14px rgba(0,0,0,0.5)' : '0 2px 14px rgba(60,45,20,0.12)'),
    ...style,
  }}>
    {children}
  </div>
);

/* ─── PAGE STACK — stacked-paper depth behind the hero/agreement cards ─── */
const PageStack = ({ children, isDark }) => (
  <div style={{ position: 'relative' }}>
    <div style={{
      position: 'absolute', top: 8, left: 6, right: -6, bottom: -8,
      background: isDark ? 'linear-gradient(160deg,#171208,#191409,#1d170c)' : 'linear-gradient(160deg,#e6dcc0,#e0d5b6,#dbceac)',
      borderRadius: T.radius.lg, border: `1px solid ${isDark ? '#332a18' : '#c4b489'}`, zIndex: 0,
    }} />
    <div style={{
      position: 'absolute', top: 4, left: 3, right: -3, bottom: -4,
      background: isDark ? 'linear-gradient(160deg,#1a1509,#1c170b,#20190d)' : 'linear-gradient(160deg,#ece2c6,#e6dcbe,#e1d6b4)',
      borderRadius: T.radius.lg, border: `1px solid ${isDark ? '#3a3018' : '#d4c495'}`, zIndex: 1,
    }} />
    <div style={{ position: 'relative', zIndex: 2 }}>{children}</div>
  </div>
);

/* ─── CONTRACT FIELD — labeled term rows (Party A / Term / Progress, etc.) ─── */
const ContractField = ({ label, value, isDark, wide = false, last = false }) => {
  const ink = isDark ? '#dcd0ae' : '#2a2210';
  const faint = isDark ? 'rgba(220,208,174,0.12)' : 'rgba(42,34,16,0.1)';
  const muted = isDark ? '#a89878' : '#7a6b48';
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'baseline' }}>
      <span style={{
        fontSize: 9, color: muted, minWidth: wide ? 87 : 52, fontWeight: 800,
        textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0, fontFamily: T.font.sans,
      }}>{label}</span>
      {/* Round 14: `last` drops this field's own underline — the LAST
          ContractField in the list (Next Up) sits right above the
          signature row, and its underline read as a deliberate divider
          separating the two, which wasn't the intent. Only that one
          instance opts out; the rest of the list keeps its fill-in-the-
          blank underlines. */}
      <div style={{ flex: 1, borderBottom: last ? 'none' : `1px solid ${faint}`, paddingBottom: 2, minHeight: 13 }}>
        <span style={{ fontSize: 10, color: ink, fontFamily: T.font.doc }}>{value}</span>
      </div>
    </div>
  );
};

/* ─── WOBBLY PROGRESS RING — hand-drawn signature-ceremony ring. Restored
   from an earlier version of this file (git history) and restyled to the
   current red ACCENT token — the ring itself is the document's seal ("the
   progress is the seal"), which is why wax-seal.png no longer also
   appears in this same signature row (see NegotiationAgreement). ─── */
const WobblyProgressRing = ({ pct, size = 110, color = ACCENT, isDark }) => {
  const r = size / 2;
  const numPoints = 72;
  const innerR = r - 12;
  // Round 4: much thinner stroke, matching the hand-drawn signature-squiggle
  // weight already used for this skill elsewhere (config.sales-negotiation.jsx
  // uses strokeWidth="1.4") instead of round 3's thick size-proportional
  // stroke. Wobble dialed back to suit the thinner line — the same large
  // wobble amplitude that read as "presence" on a thick stroke reads as
  // jitter on a thin one.
  const wobble = 2.6;
  const STROKE = 1.5;
  // Loop i from 0..numPoints-1 (not numPoints+1) — the previous version
  // walked a full 360° AND appended an explicit last point nearly
  // (but not exactly) coincident with the first, THEN closed with 'Z',
  // which adds its own implicit segment back to the start. That's two
  // separate near-duplicate points at the seam — the actual cause of the
  // "two dots" artifact at low pct, where a near-zero-length round-capped
  // dash renders a visible dot at each of them. A single 'Z' closing back
  // to the one true start point removes the duplicate seam entirely.
  const generatePath = () => Array.from({ length: numPoints }, (_, i) => {
    const angle = (i / numPoints) * Math.PI * 2 - Math.PI / 2;
    const noise = Math.sin(i * 2.3 + 1.1) * wobble + Math.cos(i * 3.7 + 0.4) * wobble * 0.5;
    const rad = innerR + noise;
    return `${i === 0 ? 'M' : 'L'}${(r + Math.cos(angle) * rad).toFixed(2)},${(r + Math.sin(angle) * rad).toFixed(2)}`;
  }).join(' ') + 'Z';
  const approxCirc = 2 * Math.PI * innerR;
  const dashFill = (pct / 100) * approxCirc;
  const ink = isDark ? '#dcd0ae' : '#2a2210';
  return (
    <div style={{ position: 'relative', width: size, height: size, filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.18))' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <path d={generatePath()} fill="none" stroke={isDark ? 'rgba(220,208,174,0.22)' : 'rgba(42,34,16,0.16)'} strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round" />
        {/* Only rendered once there's real progress — at pct=0 this path's
            dash length is 0, and even skipping it here (rather than just
            relying on a zero-length dasharray) is the belt-and-suspenders
            fix for the phantom-dot issue: no path, no dot, regardless of
            how the browser handles a degenerate dash. */}
        {pct > 0 && (
          <path d={generatePath()} fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round"
            strokeDasharray={`${dashFill} ${approxCirc * 1.5}`}
            style={{ transition: 'stroke-dasharray 1.2s cubic-bezier(0.22,1,0.36,1)' }} />
        )}
        <text x={r} y={r - 3} textAnchor="middle" fontSize={size * 0.16} fontWeight="900" fill={ink} fontFamily={T.font.doc}>{pct}%</text>
        <text x={r} y={r + size * 0.12} textAnchor="middle" fontSize={size * 0.075} fontWeight="700"
          fill={isDark ? 'rgba(220,208,174,0.6)' : 'rgba(42,34,16,0.5)'}
          fontFamily="system-ui, sans-serif" letterSpacing="0.12em">SIGNED</text>
      </svg>
    </div>
  );
};

/* ─── PAPERCLIP — inline SVG, clipped onto each scenario card's corner ─── */
const Paperclip = ({ isDark }) => (
  <svg width="20" height="42" viewBox="0 0 20 42" fill="none" style={{
    position: 'absolute', top: -12, left: 18, transform: 'rotate(-9deg)',
    filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.3))', pointerEvents: 'none',
  }}>
    <path
      d="M10 3 C5 3 2 6.5 2 11.5 L2 30 C2 34.5 5.5 37.5 10 37.5 C14.5 37.5 17 34 17 30 L17 10.5 C17 8 15.3 6.3 12.8 6.3 C10.3 6.3 8.5 8 8.5 10.5 L8.5 28.5"
      stroke={isDark ? '#8c8068' : '#9b8a5f'} strokeWidth="2.4" fill="none" strokeLinecap="round"
    />
  </svg>
);

/* ─── HANDWRITTEN BUTTON — shared by the hero CTA and the final CTA ─── */
const HandwrittenButton = ({ children, onClick, color = ACCENT, style = {} }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '11px 22px', background: hov ? color : 'transparent',
        color: hov ? '#fff' : color, border: `2px solid ${color}`,
        borderRadius: T.radius.sm, fontSize: 18, fontWeight: 700,
        cursor: 'pointer', fontFamily: T.font.script, letterSpacing: 'normal',
        transition: T.trans, transform: hov ? 'translateY(-2px)' : 'none',
        boxShadow: hov ? `0 8px 20px ${color}44` : 'none',
        ...style,
      }}
    >
      {children}
    </button>
  );
};

/* ══════════════════════════════════════════
   HERO — full-bleed, flush with the top of the page (rendered outside the
   page's padded wrapper — see MAIN), inside the same pagePad gutter every
   section below it uses, so the left edge lines up.

   Round 15: rebuilt again — desk-background.jpg (round 10 and earlier) and
   manifesto-background.png (round 11-14, one full-bleed image behind the
   whole hero) are BOTH retired now. Neither is referenced anywhere in this
   file anymore. Replaced with two separate paper-sheet images generated
   with their own alpha-channel torn edges already baked in (confirmed by
   reading each file's own pixel data — both are true RGBA with (0,0,0,0)
   at the corners, not a white background needing to be chroma-keyed):
   - hero-paper-left.png sits behind the text column. Straight top/left
     edges align with the hero's own top-left corner; torn bottom/right
     edges face inward, so it reads as the "front" sheet the text sits on
     (see its own block below).
   - hero-contract-panel.png is a photographic desk scene (lamp, globe,
     books, the "Castle Atlas" contract) with an irregular torn silhouette
     already baked into its own alpha shape — rendered at its native
     aspect ratio, no crop/mask forcing it into a rectangle, positioned to
     the right of the text column with its own ragged edge facing back
     toward hero-paper-left (see its own block below).
   Text/contrast: still the same ink/muted tokens every other card on this
   page already uses (round 11 put those in when the background first went
   from a dark photo to a light one) — genuinely simpler now than it was
   with a single background image, since there's no longer a "is the photo
   dark enough here for light text" question anywhere to answer. The old
   bottom fade-to-`paper` and the left text-legibility wash both existed
   specifically to blend a single busy photo into the page and behind the
   text — neither one has a job left once the text sits on its own opaque
   paper sheet instead, so both are gone (checked: `paper` had no other
   caller in this component before removing the prop).
   live-demo.txt panel removed (round 3) — it implied live interactivity
   the landing page doesn't actually have.
══════════════════════════════════════════ */
const Hero = ({ isMobile, isTablet, onStart, totalReps, hasStarted, overallPct }) => {
  // Round 15: no isDark prop anymore, and ink/muted are NOT isDark-
  // conditional, unlike every other ink/muted pair on this page. Those
  // flip because their card backgrounds flip (paper-texture.jpg gets a
  // dark tint overlay in dark mode). The hero's two paper sheets don't —
  // real paper doesn't change color because a room is dark, and neither
  // image is theme-swapped — so this background stays light-parchment in
  // both themes, and using the dark-mode ink token here put light cream
  // text on light cream paper (confirmed: "CLOSE MORE DEALS." nearly
  // disappeared in a dark-mode screenshot before this fix). Always the
  // same tones every other light background on this page uses.
  const ink = '#2a2210';
  const muted = '#7a6b48';
  /* StampBadge / h1 / p / CLAUSES / CTA row — identical at every width.
     Pulled into a variable, not duplicated, because below 1024 it now
     nests one level deeper (inside the position:relative wrapper that
     also carries the inset paper sheet — see the left-sheet block below)
     while desktop keeps its original single maxWidth:640 wrapper. */
  const heroText = (
    <>
      <div style={{ marginBottom: 18 }}>
        <StampBadge color={ACCENT}>Live Skill</StampBadge>
      </div>
      <h1 style={{
        fontSize: 'clamp(1.9rem, 4.2vw, 3rem)', fontWeight: 900,
        color: ink, fontFamily: T.font.cond, lineHeight: 1.05,
        letterSpacing: '-0.01em', textTransform: 'uppercase', margin: '0 0 16px',
      }}>
        Close more deals.<br />
        <span style={{ color: ACCENT }}>Negotiate like a pro.</span>
      </h1>
      <p style={{
        fontSize: 15.5, color: muted, lineHeight: 1.65,
        marginBottom: 22, fontFamily: T.font.sans,
      }}>
        Practice real sales scenarios against an AI that pushes back like a
        real buyer. Cold calls, objections, closing, enterprise deals —
        {' '}{totalReps} reps, scored on technique in real time.
      </p>

      <div style={{ marginBottom: 22 }}>
        <div style={{
          fontSize: 10, fontWeight: 800, color: muted,
          textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12,
        }}>What you will learn</div>
        {CLAUSES.map((c, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8,
          }}>
            <span style={{ color: ACCENT, fontWeight: 900, flexShrink: 0, fontFamily: T.font.doc }}>§{i + 1}</span>
            <span style={{ fontSize: 14, color: muted, lineHeight: 1.5, fontFamily: T.font.sans }}>{c}</span>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ filter: `drop-shadow(0 6px 16px rgba(0,0,0,0.5))` }}>
          <HandwrittenButton onClick={onStart} color={ACCENT} style={{ fontSize: 21, padding: '13px 28px' }}>
            <Phone size={16} /> {getPrimaryCtaCopy(hasStarted, overallPct)}
          </HandwrittenButton>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          {[
            { icon: Scale, label: `${LIVE_SCENARIOS.length} scenarios` },
            { icon: Mic, label: `${totalReps} reps` },
            { icon: Brain, label: 'AI-scored' },
          ].map(({ icon: Icon, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon size={13} color={muted} />
              <span style={{ fontSize: 12, fontWeight: 600, color: muted }}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
  return (
    <div className="rail-clearance" style={{
      position: 'relative',
      /* Bottom padding drops to 56px below 1024 (was 92px mobile / 128px
         desktop, both applied unconditionally by isMobile — so the 640-1024
         tablet range got the 128px desktop figure too). Below 1024 the left
         sheet is now sized off the TEXT (see its own block below), which is
         shorter than the old hero-width-relative sheet, so 128px of padding
         under it left a large empty gap between the sheet's bottom edge and
         the next section — see the round-18 screenshot. Top padding is
         untouched, still keyed off isMobile alone. */
      padding: `${isMobile ? '40px' : '64px'} 0 ${isTablet ? '56px' : '128px'}`,
      overflow: 'hidden',
    }}>
      {/* rail-clearance on THIS root, not the page background further up
          (which stays full-bleed under the rail) — the two absolutely-
          positioned paper sheets below are both positioned relative to
          THIS box's own padding origin, so shifting it with a margin
          moves them and the text column together, preserving the exact
          alignment between the text and the sheet it sits on. */}
      {/* Right sheet — hero-contract-panel.png.
          Round 17: now the FRONT piece at the seam (zIndex:2, above the
          left sheet's 1) — reversed from round 15's original choice.
          Rendered at native aspect ratio (width set, height auto) — no
          object-fit crop — so the torn silhouette baked into the PNG's
          own alpha stays exactly as generated.
          Round 18: guard changed !isMobile -> !isTablet. Between 640 and
          1024 this was still rendering — the left:calc(...) below resolves
          to 794px there, which is almost entirely off a hero that narrow,
          so it was invisible anyway but still in the DOM. Hidden below
          1024 to match where it actually has room to sit.
          Round 17: width cap corrected 1720px -> 1420px (the 1720 figure
          was well past anything the 69% clause could ever hit at any
          real viewport, so it was doing nothing there — but it meant this
          sheet had no actual ceiling, which combined with the left
          sheet's own oversizing below made it barely visible at all: at
          a typical desktop width the left sheet's ~1200px reach nearly
          spanned the whole hero, so — even before this round's z-index
          swap — almost the entire panel sat underneath it. Confirmed via
          screenshot before touching either number.
          Positioning changed right:-2% -> left:calc(...). That put the
          panel's position as a function of the HERO's own (variable)
          width, same category of bug as the old text-legibility wash two
          rounds ago (see hero-paper-left's history): at 1400px there was
          no visible problem, but checking narrower common desktop widths
          (1200px, 1100px) — not just the one width already screenshotted
          — showed the panel's left edge creeping further and further into
          the text column as the hero narrows, to the point of sitting
          directly over "real buyer. Cold calls," at 1100px, unreadable
          against the photo underneath. Now that this sheet also became
          the FRONT layer at the seam (this same round, above), the
          collision reads worse than it would have before — it's the
          layer now painting over the text there.
          Anchored from the LEFT instead, past the text column's own
          fixed footprint (640 maxWidth + 24px pagePad + a 36px gap) —
          not a fraction of the hero's width, which is the actual
          variable that was causing the drift. Guarantees clearance at
          any hero width; if the hero is too narrow for the panel's own
          width to fully fit, it just clips against the hero's own
          overflow:hidden right edge instead of reaching backward over
          the text. */}
      {!isTablet && (
        <img
          src={`${ASSET}/hero-contract-panel.png`}
          alt=""
          aria-hidden="true"
          style={{
            position: 'absolute', top: '-10%', left: 'calc(24px + 540px + 230px)',
            width: 'min(69%, 1420px)', height: 'auto',
            zIndex: 2, pointerEvents: 'none',
          }}
        />
      )}

      {/* Left sheet — hero-paper-left.png. Two regimes, switched on isTablet
          (window.innerWidth < 1024) — no new breakpoint.

          DESKTOP (>=1024): unchanged since round 17. Rendered as a hero-
          width-relative sibling behind the whole content column — top:-46,
          left:-46, width min(1195px,93%), height auto (NOT object-fit:cover:
          cover scales to fill height and crops the excess width off the
          RIGHT side, exactly where the torn edge lives — confirmed by
          screenshotting the seam before catching it, back in round 15).
          880px/88% -> 1195px/93% and top:0/left:0 -> top:-46/left:-46 both
          happened in later rounds not reflected here before now; this sheet
          reaches ~1195px at typical desktop widths, comfortably past the
          640px text column + padding without the round-16 overshoot that
          spanned nearly the entire hero.

          BELOW 1024 (round 18): the desktop sheet is sized off the HERO's
          width with height:auto, so its height shrinks as the viewport
          narrows — while the text column does the opposite, wrapping onto
          more lines as it narrows. Around 1000px wide those cross and the
          text overflows the sheet's bottom onto bare page background.
          Separately, top:-46/left:-46 was never gated to desktop, so on
          mobile (sheet width:100%) it sat offset up-left of the text
          column, hanging text off the top and right edges. Confirmed both
          at 900px and 400px before changing anything.

          Round 20 tried a positioned div with -34 insets inside a
          position:relative wrapper, on the theory that a non-replaced
          element (unlike round 18's <img>) really does solve width/height
          from four insets. That part was correct, but the div still came
          up short in practice: it wasn't sitting in the containing block
          the insets assumed, so coverage still had to be verified rather
          than being true by construction — the same failure mode twice,
          just moved one layer down.

          Round 21: stopped trying to get an absolutely-positioned box to
          cover a box of unknown height and switched to a mechanism with
          NO positioning at all below 1024 — no containing block to get
          wrong, no inset to measure. The paper is now a CSS background
          directly on the text column's own div, with padding as the
          bleed: a background box is, structurally, never shorter than
          its own content, so "does the sheet cover the text" stops being
          a question that needs an answer. backgroundSize:'100% 100%'
          (not 'cover') stretches the image non-uniformly to fill that
          box exactly — the paper grain and torn-edge scallops elongate
          slightly, imperceptible in exchange for covering the text at
          every width. Do NOT swap in 'cover' here — cover crops the torn
          edges off, the exact bug two paragraphs up. The 34px padding
          shifts the text right/down below 1024 — that's the intended
          margin between the text and the torn edge, not a side effect. */}
      {!isTablet && (
        <img
          src={`${ASSET}/hero-paper-left.png`}
          alt=""
          aria-hidden="true"
          style={{
            position: 'absolute', top: -46, left: -46,
            width: 'min(1195px, 93%)', height: 'auto',
            zIndex: 1, pointerEvents: 'none',
          }}
        />
      )}

      <div style={{ padding: pagePad(isMobile), position: 'relative', zIndex: 2 }}>
        <div style={{
          maxWidth: 640,
          padding: isTablet ? '34px 34px 40px' : 0,
          backgroundImage: isTablet ? `url(${ASSET}/hero-paper-left.png)` : 'none',
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
        }}>
          {heroText}
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   NEGOTIATION AGREEMENT — one column of the 3-up row (see MAIN). Round 3
   rebuild: this used to just restate the hero's own "4 scenarios / N
   reps" pitch as a "Program" term — genuinely redundant with the hero
   meta row right above it. Now it's a personal progress ledger instead:
   real numbers about THIS learner (reps done, average score, streak,
   how many of the 4 scenarios are fully complete, what's next), nothing
   that just repeats the hero's generic pitch.

   Signature/date no longer show a hasStarted-gated "awaiting signature..."
   placeholder — both always render something real (initials monogram,
   today's date), side by side in one row rather than stacked, with
   WobblyProgressRing as the centerpiece above them.
══════════════════════════════════════════ */
const NegotiationAgreement = ({
  isDark, hasStarted, overallPct, totalCompleted,
  streak, avgScore, scenariosComplete, totalScenarios, resumeTarget,
  learnerInitials,
}) => {
  const ink = isDark ? '#dcd0ae' : '#2a2210';
  const muted = isDark ? '#a89878' : '#7a6b48';
  const faint = isDark ? 'rgba(220,208,174,0.14)' : 'rgba(42,34,16,0.1)';
  const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <PageStack isDark={isDark}>
      <ParchmentCard isDark={isDark} style={{ height: '100%' }}>
        {/* Round 12: this header used to sit on its own semi-transparent
            background strip, visually separated from the rest of the card
            — Skill Radar and Deal Intelligence never had that, their
            headers just sit on the shared card background. Dropped here
            too so all three cards in the row read the same way.
            Round 13: the status StampBadge that used to live here swapped
            places with wax-seal.png from the signature row below — the
            seal became this card's big focal badge, the status text moved
            down to the small slot the seal used to occupy.
            Round 19: wax-seal.png replaced with the real Castle Atlas seal
            (castle-atlas-seal.png, moved from copywriting/seal/ to the
            shared /brand/ folder — this page is the second consumer, not
            the first). Filter swapped from mixBlendMode to a plain
            drop-shadow: this is a physical wax object sitting on the
            document, not linework meant to blend into the page the way
            the pen-and-ink icons do, and multiply would make the wax
            read as translucent, which a real seal never is.
            Round 20: 56x56 lost the seal's embossed detail entirely — it
            read as a flat red dot in both themes. Raised to 72x72;
            screenshotted at 56/72/84 in both themes before settling
            here. See its own note just below. */}
        {/* Round 14: eyebrow brought in line with Skill Radar / Deal
            Intelligence's shared pattern — small Brain icon + muted 8px
            label (was its own bigger accent-colored, icon-less style).
            Those two aren't quite identical either (Skill Radar's is 9px,
            Deal Intelligence's is 8px) — matched to Deal Intelligence's,
            the more recently-tuned of the two; flagging the 1px gap
            between them in case that's worth closing too. */}
        <div style={{
          padding: '7px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Brain size={11} color={ACCENT} />
            <span style={{
              fontSize: 8, fontWeight: 800, color: muted,
              textTransform: 'uppercase', letterSpacing: '0.07em',
            }}>Castle Atlas Learning Systems</span>
          </div>
          <img
            src="/brand/castle-atlas-seal.png" alt="" aria-hidden="true"
            style={{
              width: 72, height: 72, objectFit: 'contain', flexShrink: 0,
              filter: isDark
                ? 'drop-shadow(0 3px 5px rgba(0,0,0,0.65))'
                : 'drop-shadow(0 2px 4px rgba(60,45,20,0.38))',
            }}
          />
        </div>
        <div style={{ padding: '11px 13px', position: 'relative' }}>
          <h2 style={{
            fontSize: 'clamp(0.85rem, 1.4vw, 1.02rem)', fontWeight: 900,
            color: ink, fontFamily: T.font.cond, letterSpacing: '-0.01em',
            margin: '0 0 2px', lineHeight: 1.15, textTransform: 'uppercase',
          }}>Negotiation Agreement</h2>
          {/* Round 14: this subtitle is now the reference style Skill
              Radar's and Deal Intelligence's own subtitle-level text were
              brought in line with — see those components. */}
          <p style={{ fontSize: 10, color: muted, fontStyle: 'italic', margin: '0 0 9px', fontFamily: T.font.doc }}>
            Your standing, not the pitch
          </p>

          <div style={{
            fontSize: 8, fontWeight: 800, color: muted,
            textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5,
          }}>Terms of Mastery</div>
          <div style={{ marginBottom: 9 }}>
            <ContractField label="Reps Done" value={totalCompleted} isDark={isDark} />
            <ContractField label="Avg Score" value={avgScore > 0 ? avgScore : '—'} isDark={isDark} />
            <ContractField label="Streak" value={streak > 0 ? `${streak}d` : '—'} isDark={isDark} />
            <ContractField label="Scenarios" value={`${scenariosComplete} / ${totalScenarios} at 100%`} isDark={isDark} />
            <ContractField label="Next Up" value={resumeTarget?.title ?? '—'} isDark={isDark} last />
          </div>

          {/* Signature row — Learner / status / Date. Both text sides
              always render something real (initials monogram, today's
              date) — no "awaiting signature..." text. Round 11 moved
              WobblyProgressRing out of this row entirely (into
              FinalCTAColumn, then round 12 into StatsColumn — see those
              components). Round 13: the small middle slot the seal used to
              occupy now holds the In Progress/Unsigned status badge,
              swapped down from the header (see that div's own comment). */}
          <div style={{ paddingTop: 4, display: 'flex', width: '100%', gap: 10 }}>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{
                borderBottom: `1px solid ${faint}`, paddingBottom: 3, minHeight: 20,
                display: 'flex', alignItems: 'flex-end',
              }}>
                <span style={{
                  fontSize: 15, fontWeight: 700, color: ACCENT, fontFamily: T.font.script,
                  lineHeight: 1, transform: 'rotate(-3deg)', display: 'inline-block',
                }}>{learnerInitials}</span>
              </div>
              <div style={{
                fontSize: 8, color: muted, textTransform: 'uppercase',
                letterSpacing: '0.08em', marginTop: 2,
              }}>Learner Signature</div>
            </div>

            <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'center' }}>
              {hasStarted
                ? <StampBadge color="#16a34a" rotate={-3}>In Progress</StampBadge>
                : <StampBadge color={muted} rotate={-3}>Unsigned</StampBadge>}
            </div>

            <div style={{ flex: 1, textAlign: 'right' }}>
              <div style={{
                borderBottom: `1px solid ${faint}`, paddingBottom: 3, minHeight: 20,
                display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
              }}>
                <span style={{ fontSize: 9, color: ink, fontFamily: T.font.doc }}>{today}</span>
              </div>
              <div style={{
                fontSize: 8, color: muted, textTransform: 'uppercase',
                letterSpacing: '0.08em', marginTop: 2,
              }}>Date</div>
            </div>
          </div>

          {overallPct >= 100 && (
            <div style={{ position: 'absolute', top: 11, right: 12 }}>
              <StampBadge color={ACCENT} rotate={-12}>Closed</StampBadge>
            </div>
          )}
        </div>
      </ParchmentCard>
    </PageStack>
  );
};

/* ─── SCENARIO CARD ───
   Round 13: this icon's mixBlendMode used to be unconditional 'multiply' —
   fixed to isDark ? 'screen' : 'multiply', matching every other icon on
   this page. Two compounding problems, not one: (1) icon-*.png files are
   AI-generated with a flat opaque white canvas baked in (PNG color type
   2, RGB, no alpha channel at all — checked the file header directly, not
   assumed), so 'multiply' in dark mode was compounding two darkening
   passes — the icon's own dark linework multiplied against ParchmentCard's
   ALREADY-darkened dark-mode overlay — reading as "dark and hard to make
   out" even though the white canvas itself multiplies away cleanly in
   both themes. (2) Fixed at the source: all 6 icon PNGs on this page (the
   4 scenario icons, the Deal Intelligence rook, the quill) got a real
   alpha channel via chroma-keying out that white canvas, matching the fix
   applied to wax-seal.png this same round. With real transparency, screen
   in dark mode lightens the linework against the dark card instead of
   darkening it further, and — this was the part a blend-mode change alone
   couldn't fix — no leftover white canvas to reveal as a box the way
   screen mode would on an opaque source (see wax-seal.png's own note). ─── */
const ScenarioCard = ({ scenario, isDark, done, onClick, comingSoon = false }) => {
  const [hov, setHov] = useState(false);
  const { id, num, title, desc, reps } = scenario;
  const iconArt = getIconArt(id, isDark);
  const ink = isDark ? '#dcd0ae' : '#2a2210';
  const muted = isDark ? '#a89878' : '#7a6b48';
  const pct = Math.round((done / reps) * 100);

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={comingSoon ? undefined : onClick}
      aria-disabled={comingSoon || undefined}
      style={{ cursor: comingSoon ? 'default' : 'pointer', position: 'relative', paddingTop: 10, ...(comingSoon ? { opacity: 0.62, filter: 'saturate(0.55)' } : {}) }}
    >
      <Paperclip isDark={isDark} />
      <ParchmentCard isDark={isDark} hovered={hov && !comingSoon} style={{
        transform: hov && !comingSoon ? 'translateY(-4px)' : 'none', height: '100%',
      }}>
        {comingSoon && (
          <span data-testid="scenario-coming-soon" style={{
            position: 'absolute', top: 12, left: 14, zIndex: 2, padding: '3px 9px', borderRadius: 999,
            fontSize: 10, fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase',
            fontFamily: T.font.cond, color: isDark ? '#1f1911' : '#fff7e0', background: isDark ? '#c9b98a' : '#7a6b48',
          }}>Coming soon</span>
        )}
        <div style={{
          position: 'absolute', top: 10, right: 14, zIndex: 1,
          fontSize: 30, fontWeight: 900, color: isDark ? 'rgba(220,208,174,0.16)' : 'rgba(42,34,16,0.12)',
          fontFamily: T.font.cond, lineHeight: 1,
        }}>{num}</div>

        {/* Icon banner — dominant visual element of the card, matching the
            quill flourish's presence in the Final CTA band. Round 4: no
            longer has its own separate paper-texture background or a
            border dividing it from the text below — it was re-applying
            the exact same texture ParchmentCard already renders for the
            whole card, and two independently-positioned copies of that
            image plus a hard border is what read as two stacked panels
            instead of one composition. The icon now just sits on the
            card's own shared background. Category tag removed entirely
            (round 4) — wasn't adding anything. */}
        <div style={{
          height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <img src={iconArt.src} alt="" aria-hidden="true" style={{ width: 188, height: 188, objectFit: 'contain', ...iconArt.style }} />
        </div>

        <div style={{ padding: '4px 18px 16px', position: 'relative' }}>
          <h3 style={{
            fontSize: 18, fontWeight: 900, color: ink,
            fontFamily: T.font.cond, letterSpacing: '-0.01em', margin: '0 0 8px',
            textTransform: 'uppercase',
          }}>{title}</h3>

          <p style={{
            fontSize: 12.5, color: muted, lineHeight: 1.55, margin: '0 0 14px',
            fontFamily: T.font.sans,
          }}>{desc}</p>

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            paddingTop: 12, borderTop: `1px solid ${isDark ? '#3a3220' : '#e2d6ae'}`,
          }}>
            <span style={{
              fontSize: 11, fontWeight: 700, color: done > 0 ? ACCENT : muted,
              fontFamily: T.font.mono,
            }}>
              {comingSoon ? 'Not open yet' : done > 0 ? `${done}/${reps} reps · ${pct}%` : `${reps} reps`}
            </span>
            <span style={{
              fontSize: 12, fontWeight: 800, color: ACCENT, display: 'flex', alignItems: 'center', gap: 4,
              fontFamily: T.font.cond,
            }}>
              {comingSoon ? 'Coming soon' : `${CTA_VERBS[scenario.id] ?? (done > 0 ? 'Continue' : 'Start')} →`}
            </span>
          </div>

          {done > 0 && (
            <div style={{ height: 3, background: isDark ? '#3a3220' : '#e2d6ae', borderRadius: 2, marginTop: 10, overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${pct}%`,
                background: ACCENT, borderRadius: 2, transition: 'width 0.6s ease',
              }} />
            </div>
          )}
        </div>
      </ParchmentCard>
    </div>
  );
};

/* ─── DEAL INTELLIGENCE — one column of the 3-up row. Rounds 4-8 fought
   the rook's sizing across a width-based split, then a height-based one,
   landing on a fixed-height icon banner stacked ABOVE the header+bullets.
   Round 11 restructures the composition itself, not just the sizing:
   header (eyebrow + title) now spans the full card width at the very top,
   or the rook read as randomly placed above it; below the header, the
   rook and the bullet list sit side by side in one row instead of the
   text being sandwiched in the card's middle. The bullets are spread with
   justify-content:space-between across that row's full height instead of
   clumped with small gaps, so they visually span the same height as the
   rook next to them. The rook is sized off BOTH width (flex-basis, safe —
   this is a plain horizontal row, not the percentage-height-in-an-
   indeterminate-column-flex situation that caused rounds 7-8's bug) and
   height:100% of its own row — that row's height comes from the bullets'
   real text content via normal flex stretch, not from a chain of
   percentage heights against an indeterminate ancestor, so it doesn't
   reintroduce that bug. A minHeight floor on the row is extra insurance
   regardless. Bullet marker: previously had no explicit font-size (so it
   inherited an ambient size that didn't match the 10px text next to it)
   with just a marginTop nudge trying to compensate — now sized to match
   the text exactly, with a much smaller nudge, so it actually aligns with
   the first line instead of the fontSize mismatch making the nudge value
   fight itself. ─── */
const DealIntelligence = ({ isDark, isTablet }) => {
  const muted = isDark ? '#a89878' : '#7a6b48';
  const dealIconArt = getIconArt('deal-intelligence', isDark);
  return (
    <ParchmentCard isDark={isDark} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header — full card width, above the rook/bullets row. Round 12:
          asked for ~15px more closeness to the row below — the real gap
          here (header's own bottom padding + the row's top padding) only
          totaled ~8px to begin with, so there wasn't 15px to remove
          without the two overlapping. Closed what's actually there
          instead (both paddings to 0) rather than forcing a negative
          margin to hit an exact number that isn't available.
          Round 14: "What the AI is tracking" was a bold h3 (clamp ~14-16px,
          weight 900, T.font.cond) — this card's only title-weight element,
          nothing else here plays that role. Restyled down to match
          Negotiation Agreement's subtitle (10px, italic, T.font.doc) per
          this round's explicit instruction to unify the 3 cards' subtitle-
          level text using that as the reference — flagging that this
          genuinely demotes what was this card's title, not just a subtitle
          tweak, in case that wasn't the intent. `ink` (the color token this
          text used) is now unused in this component and removed. */}
      <div style={{ padding: '10px 14px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
          <Brain size={11} color={ACCENT} />
          <span style={{
            fontSize: 8, fontWeight: 800, color: muted,
            textTransform: 'uppercase', letterSpacing: '0.07em',
          }}>Deal Intelligence</span>
        </div>
        <p style={{
          fontSize: 10, color: muted, fontStyle: 'italic',
          fontFamily: T.font.doc, margin: 0,
        }}>What the AI is tracking</p>
      </div>

      {/* Rook (left) + bullet list (right), side by side */}
      <div style={{
        flex: 1, minHeight: 90, padding: '0 12px 12px',
        display: 'flex', alignItems: 'stretch', gap: 12,
      }}>
        {/* Round 18: capped at 200px when isTablet — below 1024 the 3-up
            row collapses to one column (see MAIN), so this card goes
            full-width and the flex:'0 0 40%' rook grew enormous next to
            the still-small bullet text beside it. */}
        <div style={{
          flex: '0 0 40%', maxWidth: isTablet ? 200 : undefined,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {/* Round 19: the rook now goes through the same ICON_ART registry
              as the 4 scenario icons (key 'deal-intelligence') rather than
              a hardcoded light-only src. It was briefly the one icon with
              "no blend-mode trick needed at all" (round 18) — that held
              only because it had no dark variant yet; now that it does,
              it follows the exact same dark-variant-gets-no-mixBlendMode
              rule as everything else in the registry, not a special case. */}
          <img
            src={dealIconArt.src} alt=""
            aria-hidden="true"
            style={{ width: '100%', height: '100%', objectFit: 'contain', ...dealIconArt.style }}
          />
        </div>
        {/* Round 12: bullets were spread with justify-content:space-between
            (round 11) so they'd span the same height as the rook — no
            fixed gap value existed to literally halve. Read as too loose
            once rendered, so switched to a real, tight gap (8px) and
            justify-content:center so the tighter cluster still sits
            centered against the rook rather than pinned to the top.
            Round 14: 8px turned out too tight — nudged up ~5px to 13px,
            not back to the original loose space-between spread. */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 13 }}>
          {DEAL_INTELLIGENCE.map((line, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 5 }}>
              <span style={{ color: ACCENT, fontWeight: 900, flexShrink: 0, fontSize: 10, lineHeight: 1.4, marginTop: 1 }}>•</span>
              <span style={{ fontSize: 10, color: muted, lineHeight: 1.4, fontFamily: T.font.sans }}>{line}</span>
            </div>
          ))}
        </div>
      </div>
    </ParchmentCard>
  );
};

/* ─── SKILL RADAR — one column of the 3-up row. Real chart, live data —
   unchanged logic from before. Round 3: chart now fills the card's actual
   stretched height (flex:1 + ResponsiveContainer height="100%") instead of
   a fixed 210px pinned near the top, with a tall minHeight so it's ~4x
   more substantial even when the row's other columns don't force much
   height. Round 6: that minHeight came in about 15% too big — 420 → 357.
   Round 7 left the chart region alone to avoid a double-reduction on top
   of that. Round 8: the row's overall footprint needed a much bigger cut
   (~50%, not 15%) than round 7 attempted, and this chart — as the
   tallest natural driver of the row's height — has to be part of that or
   the row can't actually shrink. minHeight 357 → 200, with the chart's
   own internal margins/tick size trimmed to match a card this much
   smaller. ─── */
const SkillRadar = ({ isDark, hasStarted, radarDisplay, radarIsGhost }) => (
  <ParchmentCard isDark={isDark} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <div style={{ padding: '10px 12px 3px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
        <Brain size={11} color={ACCENT} />
        <span style={{
          fontSize: 9, fontWeight: 800, color: isDark ? '#a89878' : '#7a6b48',
          textTransform: 'uppercase', letterSpacing: '0.07em',
        }}>Skill Radar</span>
      </div>
      {/* Round 14: matched to Negotiation Agreement's subtitle style
          (10px, italic, T.font.doc) — was 9px, non-italic, T.font.sans. */}
      <p style={{
        fontSize: 10, color: isDark ? '#a89878' : '#7a6b48', fontStyle: 'italic', margin: 0, fontFamily: T.font.doc,
      }}>{hasStarted ? 'Your profile' : 'What mastery looks like'}</p>
    </div>
    <div style={{ position: 'relative', flex: 1, minHeight: 200, padding: '0 4px 8px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={radarDisplay} margin={{ top: 4, right: 14, bottom: 4, left: 14 }}>
          <PolarGrid stroke={isDark ? '#3a3220' : '#d9cb9e'} />
          <PolarAngleAxis dataKey="skill" tick={{
            fill: isDark ? '#a89878' : '#7a6b48', fontSize: 10, fontWeight: 600,
          }} />
          <Radar name="Full" dataKey="full" stroke="transparent"
            fill={isDark ? `${ACCENT}10` : `${ACCENT}0c`}
            fillOpacity={1} isAnimationActive={false} />
          <Radar name="You" dataKey="value" stroke={ACCENT} fill={ACCENT}
            fillOpacity={radarIsGhost ? 0.1 : 0.2}
            strokeWidth={radarIsGhost ? 2 : 2.5}
            strokeDasharray={radarIsGhost ? '5 5' : undefined}
            isAnimationActive={!radarIsGhost}
            animationBegin={300} animationDuration={1200} animationEasing="ease-out" />
          {!radarIsGhost && (
            <Tooltip formatter={v => [`${v}%`, 'Your level']}
              contentStyle={{
                background: isDark ? '#1f1911' : '#fffdf5', border: `1px solid ${isDark ? '#4a3f28' : '#c9b78f'}`,
                borderRadius: 10, fontSize: 11,
              }} />
          )}
        </RadarChart>
      </ResponsiveContainer>
      {radarIsGhost && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          // Round 16: measured against the hexagon's actual rendered
          // geometry (outermost concentric-grid polygon's own bounding
          // box), not eyeballed — the plain -50%/-50% centering put the
          // badge 4.1px below the hexagon's true center (negligible
          // horizontally, 0.16px). Nudged up to correct that, plus a 1px
          // rightward nudge as asked even though the horizontal measurement
          // was already close to dead-on.
          transform: 'translate(calc(-50% + 1px), calc(-50% - 4px))', textAlign: 'center', pointerEvents: 'none',
        }}>
          {/* Round 4: was a generic pill with copy that mixed the "sign" /
              contract metaphor with "reps" — replaced with wording that
              picks one metaphor, and the page's own StampBadge treatment
              instead of a plain pill, so it actually reads as part of this
              page rather than a generic empty-state label. Round 15:
              simplified further — "Complete a few reps to reveal your
              profile" explained itself when it didn't need to; "Locked"
              says the same thing in one word. */}
          <StampBadge color={isDark ? '#a89878' : '#7a6b48'} rotate={-4}>
            Locked
          </StampBadge>
        </div>
      )}
    </div>
  </ParchmentCard>
);

/* ─── FINAL SECTION — what used to be a single full-width FinalCTA card +
   a separate standalone FooterStatsBar below everything is now a 3-column
   row: FinalCTAColumn (A), StatsColumn (B, the old FooterStatsBar content
   moved up here), and SalesTipsColumn (C). Round 3 briefly added a third
   sticky-notes column here; round 4 removed it outright rather than
   reskinning it, and it never made it back in — round 11 finally rebuilds
   it as SalesTipsColumn, a new build rather than a restore (the round 3
   version's content isn't recoverable — see the round 5 conversation for
   why). ─── */

/* Column A — headline/subtext/button, quill roughly 2x bigger than
   before. Round 11 put WobblyProgressRing here, next to the quill; round
   12 moves it again, into StatsColumn instead (see that component) — this
   column goes back to just the quill as its one visual anchor above the
   headline. */
const FinalCTAColumn = ({ isDark, isMobile, hasStarted, overallPct, totalExercises, onContinue, firstScenarioId, resumeScenarioId }) => {
  const ink = isDark ? '#dcd0ae' : '#2a2210';
  const muted = isDark ? '#a89878' : '#7a6b48';

  return (
    <ParchmentCard isDark={isDark} style={{ height: '100%' }}>
      <div style={{ padding: '22px 20px', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
        {/* Round 4: the quill PNG has generous built-in canvas whitespace
            around the drawn feather itself, so the visual gap before the
            headline was much bigger than the (now removed) flex gap alone
            suggested. Negative margin on the heading pulls it up to
            compensate, so icon and headline read as one composition. */}
        <img
          src={`${ASSET}/icon-quill-flourish.png`} alt=""
          aria-hidden="true"
          style={{
            /* height:'auto' reserved NOTHING until the PNG decoded, so the
               block collapsed to zero and everything below it jumped once
               the image landed. The art is square (1254x1254), so
               aspect-ratio holds the exact footprint from first paint.
               Same reasoning as .story-castle-box in index.css. */
            width: isMobile ? 100 : 128, aspectRatio: '1 / 1', height: 'auto',
            alignSelf: 'flex-start',
            mixBlendMode: isDark ? 'screen' : 'multiply',
            filter: isDark ? 'invert(1) brightness(0.85)' : 'none',
            opacity: isDark ? 0.6 : 0.9,
          }}
        />

        <div style={{ marginTop: -22 }}>
          <h2 style={{
            fontSize: 'clamp(1.2rem, 2.4vw, 1.5rem)', fontWeight: 900,
            color: ink, fontFamily: T.font.cond, textTransform: 'uppercase',
            letterSpacing: '-0.01em', lineHeight: 1.1, margin: '0 0 8px',
          }}>
            {hasStarted ? 'Keep closing.' : 'Your first deal starts now.'}
          </h2>
          <p style={{
            fontSize: 13, color: muted, lineHeight: 1.55,
            margin: '0 0 16px', fontFamily: T.font.sans,
          }}>
            {hasStarted
              ? `You are ${overallPct}% through. Every rep sharpens your instinct for the next real conversation.`
              : `${totalExercises} scenarios. An AI that negotiates back. No script to memorize — just reps that build real instinct.`}
          </p>

          <HandwrittenButton
            onClick={() => onContinue(resumeScenarioId ?? firstScenarioId)}
            color={ACCENT} style={{ fontSize: 18 }}
          >
            <TrendingUp size={15} /> {getPrimaryCtaCopy(hasStarted, overallPct)}
          </HandwrittenButton>

          <p style={{
            fontSize: 10, color: muted, marginTop: 12, fontFamily: T.font.mono,
            letterSpacing: '0.02em',
          }}>
            Free to start · No credit card · Scored by AI, not a human
          </p>
        </div>
      </div>
    </ParchmentCard>
  );
};

/* Column B — the old standalone FooterStatsBar, moved up into the row.
   Round 12: the center slot is now WobblyProgressRing instead of a static
   seal image + separate "{pct}%" text label — the ring already renders
   its own percentage inside the SVG, so keeping a second, independent %
   readout next to it would just be the same number twice. Sized ~20%
   bigger than the seal it replaced (100px -> 120px) since the ring's own
   stroke-and-label composition reads smaller at a given box size than a
   solid image did. Note: the image this replaced was CASTLE_SEAL (the
   shared Copywriting seal, round 4+), not wax-seal.png — wax-seal.png
   hasn't been used on this page since round 4, when it was swapped out
   for CASTLE_SEAL; see NegotiationAgreement below for where it lands now.
   Two typefaces total (script for the learner's name, sans for everything
   else) carried over from round 4. */
const StatsColumn = ({ isDark, overallPct, learnerName, isTablet }) => {
  const muted = isDark ? '#8c7d5c' : '#8c7d5c';
  const line = isDark ? '#3a3220' : '#d9cb9e';
  const labelStyle = { fontSize: 9, color: muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6, fontFamily: T.font.sans, fontWeight: 700 };
  // Round 15: below tablet width this row had 3 items competing for
  // horizontal space (learner name, the 120px ring, "Castle Atlas") and
  // ran out of it — same isTablet flag the rest of this file already
  // tracks (window.innerWidth < 1024, see MAIN), not a new breakpoint.
  // Stacked, the ring's own divider makes more sense as a horizontal rule
  // above/below it than the vertical ones that separated it from its
  // neighbors in a row.
  const dividerStyle = isTablet
    ? { borderTop: `1px solid ${line}`, borderBottom: `1px solid ${line}`, padding: '14px 0' }
    : { borderLeft: `1px solid ${line}`, borderRight: `1px solid ${line}`, padding: '0 18px' };

  return (
    <ParchmentCard isDark={isDark} style={{ height: '100%' }}>
      <div style={{
        padding: '22px 20px', height: '100%', boxSizing: 'border-box',
        display: 'flex', flexDirection: isTablet ? 'column' : 'row',
        alignItems: 'center', justifyContent: isTablet ? 'center' : 'space-between',
        gap: isTablet ? 16 : 12,
      }}>
        {/* Learner */}
        <div style={{ flex: isTablet ? '0 0 auto' : 1, minWidth: 0, textAlign: 'center' }}>
          <div style={labelStyle}>Learner</div>
          {/* Round 16: overflow:hidden used to sit directly on the rotated
              element itself. That's a self-clip — the hidden-overflow
              region is defined in the box's OWN local coordinates before
              rotation, sized tightly around the (short, unrotated) text's
              natural bounding box, so any part of a script-font glyph's
              flourish that extends past that tight box gets cut off
              regardless of rotation. Confirmed directly: toggling
              overflow hidden->visible on this exact element (nothing else
              changed) visibly lengthened the tail of a test "X" glyph —
              proof it really was being clipped, not just an optical
              illusion from the different font-size/rotation-angle versus
              the Negotiation Agreement card's own signature initials
              (which has no overflow constraint at all and never clipped).
              Fix: split the two jobs across two elements. This outer div
              carries the ellipsis truncation (for a genuinely long name)
              against the FULL flex:1 width available, not a tight fit —
              so short names/initials have generous headroom and their
              flourish is never close to that boundary. The inner span
              carries the rotation and has no overflow of its own, so
              nothing about its own rendering gets self-clipped. */}
          <div style={{
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
          }}>
            <span style={{
              fontSize: 26, fontWeight: 700, color: ACCENT, fontFamily: T.font.script,
              lineHeight: 1.1, transform: 'rotate(-2deg)', display: 'inline-block',
            }}>
              {learnerName}
            </span>
          </div>
        </div>

        {/* Ring — the focal element, always centered */}
        <div style={{
          flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center',
          ...dividerStyle,
        }}>
          <WobblyProgressRing pct={overallPct} size={120} color={ACCENT} isDark={isDark} />
        </div>

        {/* Castle Atlas */}
        <div style={{ flex: isTablet ? '0 0 auto' : 1, minWidth: 0, textAlign: 'center' }}>
          <div style={labelStyle}>Castle Atlas</div>
          <div
            role="img" aria-label="Castle Atlas"
            style={{
              width: 24, height: 27, margin: '0 auto',
              backgroundColor: ACCENT, opacity: 0.9,
              WebkitMask: 'url(/logo.svg) center / contain no-repeat',
              mask: 'url(/logo.svg) center / contain no-repeat',
            }}
          />
        </div>
      </div>
    </ParchmentCard>
  );
};

/* Column C — NEW. A small stack of individual paper-card sales tips, one
   short punchy line each, pulled from four rough topic buckets (tactic,
   negotiation technique, sales psychology, mindset) rather than one big
   list — each tip is its own small card with a slight rotation, like a
   loose stack of index cards rather than a single note. This is a fresh
   build, not a restore of the round-3 sticky-notes column that got cut in
   round 4 — that content was never recovered (see the round 5 discussion),
   so nothing here should be assumed to match what used to be there. */
const SALES_TIPS = [
  { tag: 'Tactic', text: 'Ask the price objection back: "Compared to what?" Then let silence work.' },
  { tag: 'Technique', text: 'Anchor first. Whoever names a number first sets the frame everyone else negotiates from.' },
  { tag: 'Psychology', text: 'Buyers justify with logic but decide on emotion — sell the outcome, not the spec sheet.' },
  { tag: 'Mindset', text: 'A "no" today is data, not defeat. Log the objection, refine the next call.' },
  { tag: 'Tactic', text: 'Never split the difference by default. Concede slowly, and always trade — never just give.' },
];
const TIP_ROTATIONS = [-1.5, 1, -1, 1.5, -0.5];

const SalesTipsColumn = ({ isDark }) => {
  const ink = isDark ? '#dcd0ae' : '#2a2210';
  const muted = isDark ? '#a89878' : '#7a6b48';

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'space-between' }}>
      {SALES_TIPS.map((tip, i) => (
        <div key={i} style={{
          backgroundImage: `linear-gradient(${isDark ? 'rgba(20,15,8,0.82)' : 'rgba(255,252,240,0.14)'}, ${isDark ? 'rgba(20,15,8,0.82)' : 'rgba(255,252,240,0.14)'}), url(${ASSET}/paper-texture.jpg)`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          border: `1px solid ${isDark ? '#4a3f28' : '#c9b78f'}`,
          borderRadius: T.radius.md, padding: '10px 12px',
          transform: `rotate(${TIP_ROTATIONS[i % TIP_ROTATIONS.length]}deg)`,
          boxShadow: isDark ? '0 2px 10px rgba(0,0,0,0.4)' : '0 2px 10px rgba(60,45,20,0.1)',
          flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
          <div style={{
            fontSize: 8, fontWeight: 800, color: ACCENT, textTransform: 'uppercase',
            letterSpacing: '0.1em', marginBottom: 4,
          }}>{tip.tag}</div>
          <p style={{
            fontSize: 11, color: muted, lineHeight: 1.4, margin: 0, fontFamily: T.font.sans,
          }}>
            <span style={{ color: ink }}>“</span>{tip.text}<span style={{ color: ink }}>”</span>
          </p>
        </div>
      ))}
    </div>
  );
};

/* Round 15: ManifestoBackdrop — the manifesto-background.png low-opacity
   watermark that used to sit behind the Negotiation Agreement/Skill Radar/
   Deal Intelligence row, the scenarios grid, and the final CTA/stats/tips
   row — removed outright, not just faded further down. Each of those three
   sections was individually wrapped in an instance of it (see MAIN); all
   three wrappers are gone along with the component, so those sections now
   sit on plain blank background. manifesto-background.png itself is fully
   retired — Hero's own background also moved off it this same round (see
   Hero), so nothing in this file references that asset anymore. */

/* ══════════════════════════════════════════
   MAIN
══════════════════════════════════════════ */
const SalesNegotiationLanding = () => {
  usePageTitle('Sales & Negotiation | Castle Atlas');
  const navigate = useNavigate();
  const { completedExercises = [], loading: progressLoading } = useProgress();
  // Same source Dashboard.jsx/UserDropdown already use for the signed-in
  // user's display name — profiles.full_name, falling back to the email
  // local-part, then a generic label if fully signed out.
  const { user, profile } = useAuth();
  const learnerName = profile?.full_name?.trim() || user?.email?.split('@')[0] || 'Guest';
  const learnerInitials = getInitials(learnerName);

  /* Reads the real theme on the FIRST render. This used to be useState(false)
     corrected in an effect, so a dark-mode user's first paint (and the skeleton
     below) was light parchment for a frame before flipping dark. */
  const isDark = useDarkMode();
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const r = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth < 1024);
    };
    r();
    window.addEventListener('resize', r);
    return () => window.removeEventListener('resize', r);
  }, []);

  /* Enter the Voice Room for a given scenario. UNCHANGED from before the
     redesign — getRandomCharacter(scenarioId) returns a character drawn
     ONLY from that scenario's bucket (see @features/sales-negotiation/data).
     VoiceRoom then resolves ALL_CHARACTERS[char.id] from the
     /voice-room/:scenarioId param. No drawer, no intermediate panel —
     straight into the roleplay. */
  const enterVoiceRoom = (scenarioId) => {
    if (!isScenarioLive(scenarioId)) return;   // coming soon: nothing to start (the backend refuses it too)
    const char = getRandomCharacter(scenarioId);
    if (char?.id) {
      navigate(`/voice-room/${char.id}`);
    } else {
      // Bucket empty/unknown scenario — let VoiceRoom pick a random character.
      navigate('/voice-room');
    }
  };

  if (progressLoading) return <SalesSkeleton isDark={isDark} />;

  const paper = isDark ? '#1f1911' : '#efe6d0';

  /* Scope to sales-negotiation only for score/progress (records carry skill_id) — unchanged. */
  const skillExercises = completedExercises.filter(e => e.skill_id === 'sales-negotiation');
  const totalExercises = LIVE_SCENARIOS.reduce((s, sc) => s + sc.reps, 0);   // 85, not 160, while two scenarios are coming soon
  const totalCompleted = skillExercises.length;
  const overallPct = Math.min(100, Math.round((totalCompleted / totalExercises) * 100));
  const hasStarted = totalCompleted > 0;

  const avgScore = skillExercises.length > 0
    ? Math.round(skillExercises.reduce((s, e) => s + (e.score || 0), 0) / skillExercises.length)
    : 0;

  /* A finished conversation is stored as one user_progress row whose exercise_key
     is the character's id (ScenarioService.recordConversation -> upsertExercise).
     So a scenario's progress is the number of this skill's rows whose key is one of
     that scenario's characters. This used to filter on e.scenario_id / e.exercise_id,
     which user_progress rows do not have, so every per-scenario count was 0. */
  const getScenarioDone = id => {
    const pool = CHARACTER_POOLS[id] ?? {};
    return skillExercises.filter(e => Object.prototype.hasOwnProperty.call(pool, e.exercise_key)).length;
  };

  const getResumeTarget = () => {
    for (const sc of LIVE_SCENARIOS) {
      if (getScenarioDone(sc.id) < sc.reps) {
        return { scenarioId: sc.id, title: sc.title };
      }
    }
    return { scenarioId: LIVE_SCENARIOS[0].id, title: LIVE_SCENARIOS[0].title };
  };
  const resumeTarget = getResumeTarget();

  // How many of the 4 scenarios this learner has fully completed (all reps
  // done) — new for round 3's Negotiation Agreement rebuild; derived from
  // the same getScenarioDone() already used for each ScenarioCard, not a
  // new data source.
  const scenariosComplete = LIVE_SCENARIOS.filter(sc => getScenarioDone(sc.id) >= sc.reps).length;

  const radarData = RADAR_DATA.map(item => {
    const rel = Object.entries(SCENARIO_SKILL_MAP)
      .filter(([, s]) => s === item.skill)
      .map(([id]) => id);
    const done = rel.reduce((s, id) => s + getScenarioDone(id), 0);
    const max = rel.reduce((s, id) => s + (isScenarioLive(id) ? (CHARACTER_COUNTS[id] ?? 0) : 0), 0);   // was rel.length * 5, a placeholder that real counts would exceed
    return { ...item, value: max > 0 ? Math.min(100, Math.round((done / max) * 100)) : 0 };
  });

  /* Cold visitors see a dashed "mastery" ghost instead of an empty hexagon */
  const radarIsGhost = !hasStarted;
  const radarDisplay = hasStarted
    ? radarData
    : RADAR_DATA.map(d => ({ ...d, value: 62 }));

  const streak = 0; // wired from useStreak elsewhere if available — unchanged

  return (
    <div style={{
      minHeight: '100vh', backgroundColor: paper,
      transition: 'background-color 0.3s ease', fontFamily: T.font.sans,
    }}>
      <style>{`
        @keyframes docIn{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        .s1{animation:docIn 0.5s cubic-bezier(0.22,1,0.36,1) both}
        .s2{animation:docIn 0.5s cubic-bezier(0.22,1,0.36,1) 0.08s both}
        .s3{animation:docIn 0.5s cubic-bezier(0.22,1,0.36,1) 0.16s both}
        .s6{animation:docIn 0.5s cubic-bezier(0.22,1,0.36,1) 0.24s both}
      `}</style>

      {/* ════ 1 — HERO — full-bleed, flush with the top of the page (no
          top padding above it; the padded wrapper below only wraps
          everything AFTER the hero) ════ */}
      <div className="s1">
        <Hero
          isMobile={isMobile}
          isTablet={isTablet}
          onStart={() => enterVoiceRoom(resumeTarget?.scenarioId ?? LIVE_SCENARIOS[0].id)}
          totalReps={totalExercises}
          hasStarted={hasStarted}
          overallPct={overallPct}
        />
      </div>

      {/* Round 5: full-width below the hero — just the outer gutter
          (pagePad), no maxWidth/margin:auto reading-width column. Round 8:
          the hero's own content wrapper had the same constraint and finally
          lost it too (see Hero's comment) — pagePad itself also shrank to
          a few px, so this whole page used to sit flush against the sidebar
          instead of inset by a real margin. That no longer applies: the
          sidebar is a floating overlay rail now, not a docked column that
          consumed its own layout space, so "flush" would mean sitting
          under it. rail-clearance (a real margin, composes with pagePad's
          own padding rather than merging into it) restores actual
          clearance. Round 15: the ManifestoBackdrop watermark each section
          used to carry (manifesto-background.png at low opacity) is gone —
          see that component's own removal note near where it used to
          live, just above MAIN. Sections below the hero now sit on plain
          blank background, no texture layer. */}
      <div className="rail-clearance" style={{ padding: pagePad(isMobile) }}>

        {/* ════ 2 — NEGOTIATION AGREEMENT + SKILL RADAR + DEAL INTELLIGENCE
            — one row, three equal columns ════ */}
        <section className="s2" style={{ marginBottom: 32 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: (isMobile || isTablet) ? '1fr' : 'repeat(3, 1fr)',
            gap: 20, alignItems: 'stretch',
          }}>
            <NegotiationAgreement
              isDark={isDark} hasStarted={hasStarted}
              overallPct={overallPct} totalCompleted={totalCompleted}
              streak={streak} avgScore={avgScore}
              scenariosComplete={scenariosComplete} totalScenarios={LIVE_SCENARIOS.length}
              resumeTarget={resumeTarget} learnerInitials={learnerInitials}
            />
            <SkillRadar isDark={isDark} hasStarted={hasStarted} radarDisplay={radarDisplay} radarIsGhost={radarIsGhost} />
            <DealIntelligence isDark={isDark} isTablet={isTablet} />
          </div>
        </section>

        {/* ════ 3 — SCENARIOS ════ */}
        <section className="s3" style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <Scale size={14} color={ACCENT} />
            <span style={{
              fontSize: 11, fontWeight: 800, color: isDark ? '#a89878' : '#7a6b48',
              textTransform: 'uppercase', letterSpacing: '0.08em',
            }}>The Scenarios</span>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: 20,
          }}>
            {SCENARIOS.map(sc => (
              <ScenarioCard
                key={sc.id}
                scenario={sc}
                isDark={isDark}
                done={getScenarioDone(sc.id)}
                onClick={() => enterVoiceRoom(sc.id)}
                comingSoon={!isScenarioLive(sc.id)}
              />
            ))}
          </div>
        </section>

        {/* ════ 6 — FINAL SECTION — CTA / stats / sales tips (was the
            standalone footer bar). Round 11: 3 columns again — CTA (+
            relocated progress ring), Learner/seal/Castle Atlas stats
            (unchanged), and the new sales-tips column. ════ */}
        <section className="s6" style={{ paddingBottom: isMobile ? 16 : 32 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: 20, alignItems: 'stretch',
          }}>
            <FinalCTAColumn
              isDark={isDark} isMobile={isMobile}
              hasStarted={hasStarted} overallPct={overallPct}
              totalExercises={totalExercises}
              onContinue={enterVoiceRoom} firstScenarioId={LIVE_SCENARIOS[0].id}
              resumeScenarioId={resumeTarget?.scenarioId}
            />
            <StatsColumn isDark={isDark} overallPct={overallPct} learnerName={learnerName} isTablet={isTablet} />
            <SalesTipsColumn isDark={isDark} />
          </div>
        </section>

      </div>
    </div>
  );
};

export default SalesNegotiationLanding;
